import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
export const ConversationType = z.enum(["direct", "internal_group", "cross_group", "shop", "deal"]).meta({
    id: "ConversationType",
    description: "`shop`: a buyer's conversation with a shop (IM-4); `deal`: one inquiry's, and its order's (ADR 0020)",
});
export const ConversationParticipant = z
    .object({
    userId: Id,
    imUserId: z.string().describe("The person's messaging id, for matching messaging members"),
    displayName: z
        .string()
        .describe("The person's name, as the desktop shows them and what they write (card 149)"),
    enterpriseId: Id,
    role: z.enum(["owner", "admin", "member"]),
    side: z
        .enum(["buyer", "shop"])
        .nullable()
        .describe("In a shop or deal conversation, who speaks for the buyer and who for the shop; the buyer sees the shop"),
})
    .meta({ id: "ConversationParticipant" });
export const ConversationShop = z
    .object({
    id: Id,
    slug: z.string(),
    name: LocalizedText,
    logoUrl: z.url().nullable(),
})
    .meta({ id: "ConversationShop", description: "How the shop appears in its conversations" });
export const Conversation = z
    .object({
    id: Id,
    type: ConversationType,
    name: z.string().nullable().describe("Null for a direct chat, which shows the other person"),
    imGroupId: z.string().describe("The messaging group behind the conversation"),
    status: z
        .enum(["pending", "active", "closed"])
        .describe("`pending` until messaging has the group; usually a moment"),
    participants: z.array(ConversationParticipant),
    shop: ConversationShop.nullable().describe("The shop of a shop or deal conversation"),
    bound: z
        .object({ type: z.enum(["inquiry"]), id: Id })
        .nullable()
        .describe("What a deal conversation is about; the order made from the inquiry keeps it (ADR 0020)"),
    deal: z
        .object({
        buyer: LocalizedText.describe("The buying enterprise as it was named when it inquired"),
        product: LocalizedText.describe("The inquiry's first product"),
        lineCount: z.number().int().positive(),
        orderNumber: z.string().nullable().describe("Once the inquiry became an order"),
        orderId: Id.nullable().describe("The order the inquiry became, for the deal's panel (card 149); null on deals ordered before it was kept"),
    })
        .nullable()
        .describe("A deal conversation's subject, for its title (ADR 0020, card 063): the buyer sees the shop with the order number or product, the shop's people the buyer with it"),
    createdAt: Timestamp,
})
    .meta({ id: "Conversation" });
const Tenant = { surface: "tenant", auth: "user", permission: "conversation:create" };
export const createConversation = defineRoute({
    ...Tenant,
    id: "messaging.conversations.create",
    method: "POST",
    path: "/v1/conversations",
    marks: ["tenant", "idem"],
    summary: "Start a direct chat with a colleague or an accepted external contact (an existing one is returned), an internal group with colleagues, or a cross-enterprise group with colleagues and contacts",
    prd: ["IM-1"],
    request: {
        body: z.discriminatedUnion("type", [
            z.object({ type: z.literal("direct"), userId: Id }),
            z.object({
                type: z.literal("internal_group"),
                name: z.string().trim().min(1).max(100),
                memberUserIds: z.array(Id).min(1).max(499),
            }),
            z.object({
                type: z.literal("cross_group"),
                name: z.string().trim().min(1).max(100),
                memberUserIds: z
                    .array(Id)
                    .min(1)
                    .max(499)
                    .describe("Colleagues, and people the caller holds as contacts"),
            }),
        ]),
    },
    responses: { 201: { description: "The conversation", schema: Conversation } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "VALIDATION_FAILED",
        "CONTACT_REQUIRED",
        "PROVIDER_UNAVAILABLE",
    ],
});
export const listConversations = defineRoute({
    ...Tenant,
    id: "messaging.conversations.list",
    method: "GET",
    path: "/v1/conversations",
    marks: ["tenant"],
    summary: "The caller's conversations in the active enterprise, newest first",
    prd: ["IM-1"],
    request: {
        query: z.object({
            limit: z.coerce.number().int().min(1).max(200).default(100),
            cursor: z.string().regex(/^\d+$/).optional().describe("Opaque; pass nextCursor back"),
        }),
    },
    responses: {
        200: {
            description: "Conversations",
            schema: z.object({ items: z.array(Conversation), nextCursor: z.string().nullable() }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const getConversation = defineRoute({
    ...Tenant,
    id: "messaging.conversations.get",
    method: "GET",
    path: "/v1/conversations/{conversationId}",
    marks: ["tenant"],
    summary: "One conversation the caller takes part in",
    prd: ["IM-1", "IM-2"],
    request: { params: z.object({ conversationId: Id }) },
    responses: { 200: { description: "The conversation", schema: Conversation } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
const ConversationId = z.object({ conversationId: Id });
const Changes = [
    "AUTH_REQUIRED",
    "TENANT_REQUIRED",
    "PERMISSION_DENIED",
    "NOT_FOUND",
    "INVALID_TRANSITION",
    "VALIDATION_FAILED",
];
export const renameConversation = defineRoute({
    ...Tenant,
    id: "messaging.conversations.rename",
    method: "PATCH",
    path: "/v1/conversations/{conversationId}",
    marks: ["tenant"],
    summary: "Rename a group; its owner and admins may",
    prd: ["IM-1"],
    request: { params: ConversationId, body: z.object({ name: z.string().trim().min(1).max(100) }) },
    responses: { 200: { description: "The conversation", schema: Conversation } },
    errors: [...Changes],
});
export const addParticipants = defineRoute({
    ...Tenant,
    id: "messaging.participants.add",
    method: "POST",
    path: "/v1/conversations/{conversationId}/participants",
    marks: ["tenant", "idem"],
    summary: "Add people to a group: any participant may add colleagues; an outside person only someone who holds them as a contact, and only to a cross-enterprise group",
    prd: ["IM-1"],
    request: { params: ConversationId, body: z.object({ userIds: z.array(Id).min(1).max(100) }) },
    responses: { 200: { description: "The conversation", schema: Conversation } },
    errors: [...Changes, "CONTACT_REQUIRED"],
});
export const removeParticipant = defineRoute({
    ...Tenant,
    id: "messaging.participants.remove",
    method: "DELETE",
    path: "/v1/conversations/{conversationId}/participants/{userId}",
    marks: ["tenant"],
    summary: "Remove someone from a group: its owner may remove anyone; a group admin and an enterprise admin only people of their own enterprise",
    prd: ["IM-1"],
    request: { params: z.object({ conversationId: Id, userId: Id }) },
    responses: { 204: { description: "Removed" } },
    errors: [...Changes],
});
export const leaveConversation = defineRoute({
    ...Tenant,
    id: "messaging.conversations.leave",
    method: "POST",
    path: "/v1/conversations/{conversationId}/leave",
    marks: ["tenant", "idem"],
    summary: "Leave a group; an owner's role passes to the longest-standing participant. A direct chat cannot be left",
    prd: ["IM-1"],
    request: { params: ConversationId },
    responses: { 204: { description: "Left" } },
    errors: [...Changes],
});
/** Read state is shown for conversations of up to this many people; larger ones are `limited`. */
export const READ_STATE_LIMIT = 100;
export const getReadState = defineRoute({
    ...Tenant,
    id: "messaging.conversations.read_state",
    method: "GET",
    path: "/v1/conversations/{conversationId}/read-state",
    marks: ["tenant"],
    summary: "How far each participant has read, as a message sequence number; OpenIM tells only a reader's own devices, so the platform reports it (IM-1)",
    prd: ["IM-1"],
    request: { params: ConversationId },
    responses: {
        200: {
            description: "Read positions",
            schema: z.object({
                maxSeq: z
                    .number()
                    .int()
                    .nonnegative()
                    .describe("The conversation's latest sequence number when asked; a member whose `readSeq` reached it has read every message sent before, including one the client does not yet know the sequence number of"),
                members: z.array(z.object({ userId: Id, imUserId: z.string(), readSeq: z.number().int().nonnegative() })),
                limited: z
                    .boolean()
                    .describe(`True for conversations above ${READ_STATE_LIMIT} people, which are not reported`),
            }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "PROVIDER_UNAVAILABLE"],
});
export const conversationRoutes = [
    getReadState,
    createConversation,
    listConversations,
    getConversation,
    renameConversation,
    addParticipants,
    removeParticipant,
    leaveConversation,
];
//# sourceMappingURL=conversation.routes.js.map